import { format } from 'date-fns'
import { enGB, pt, es } from 'date-fns/locale'

import { i18n } from '@flecto/translations'

import { capitalizeString } from '../capitalizeString/capitalizeString'

export type FormatDateStringProps = {
  date: string
  locale?: 'en' | 'pt' | 'es'
  variant?:
    | 'date-full'
    | 'date-brief'
    | 'date-short'
    | 'week-full'
    | 'week-brief'
    | 'week-short'
    | 'datetime-full'
    | 'datetime-brief'
    | 'datetime-short'
    | 'time-full'
    | 'time-brief'
    | 'time-short'
}

const locales = { en: enGB, pt, es }

export function formatDateString({
  variant = 'date-brief',
  ...props
}: FormatDateStringProps) {
  if (!props.date || props.date === 'null') {
    return ''
  }

  const date = new Date(props.date)
  const locale = (props.locale ??
    i18n.language ??
    'pt') as Required<FormatDateStringProps>['locale']

  const formatShortcut = (formatString: string) => {
    return capitalizeString(
      format(date, formatString, { locale: locales[locale] })
    )
  }

  if (isNaN(date.getTime())) {
    return ''
  }

  const mapVariantsFormats = {
    'date-full': {
      en: 'MMMM d, yyyy',
      pt: 'd MMMM, yyyy',
      es: 'd MMMM, yyyy',
    },
    'date-brief': {
      en: 'MMM d yyyy',
      pt: 'd MMM yyyy',
      es: 'd MMM yyyy',
    },
    'date-short': {
      en: 'MMM d',
      pt: 'd MMM',
      es: 'd MMM',
    },
    'datetime-full': {
      en: 'MMMM d, yyyy (HH:mm)',
      pt: 'd MMMM yyyy (HH:mm)',
      es: 'd MMMM yyyy (HH:mm)',
    },
    'datetime-brief': {
      en: 'MMM d, yyyy (HH:mm)',
      pt: 'd MMM yyyy (HH:mm)',
      es: 'd MMM yyyy (HH:mm)',
    },
    'datetime-short': {
      en: 'dd/MMM/yy (HH:mm)',
      pt: 'dd/MMM/yy (HH:mm)',
      es: 'dd/MMM/yy (HH:mm)',
    },
    'week-full': {
      en: 'eeee, MMMM d yyyy',
      pt: 'eeee, d MMMM yyyy',
      es: 'eeee, d MMMM yyyy',
    },
    'week-brief': {
      en: 'eee, MMM d yyyy',
      pt: 'eee, d MMM yyyy',
      es: 'eee, d MMM yyyy',
    },
    'week-short': {
      en: 'eee, d',
      pt: 'eee, d',
      es: 'eee, d',
    },
    'time-full': {
      en: () => `${formatShortcut('HH')}h${formatShortcut('mm')}`,
      pt: () => `${formatShortcut('HH')}h${formatShortcut('mm')}`,
      es: () => `${formatShortcut('HH')}h${formatShortcut('mm')}`,
    },
    'time-brief': {
      en: 'HH:mm',
      pt: 'HH:mm',
      es: 'HH:mm',
    },
    'time-short': {
      en: 'H:mm',
      pt: 'H:mm',
      es: 'H:mm',
    },
  }

  const formatString = mapVariantsFormats[variant][locale]

  // For custom calls, like time-full
  if (typeof formatString === 'function') {
    return formatString()
  }

  return formatShortcut(formatString)
}
